import React from "react";
import { getTransactionLogs } from "services/merchant.service";
import styled from "styled-components";
import { formatMoney, modifiedFormatMoney } from "utils/money";
import { Button, Spin, Input, DatePicker, Modal } from "antd";
import { format, parseISO } from "date-fns";
import { FilterOutlined, CloseOutlined } from "@ant-design/icons";
import { debounce } from "lodash";

const TransactionTable = styled.div`
  width: 100%;
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: row;

  h4 {
    font-weight: 600;
  }
`;

const Col = styled.div`
  width: 25%;
`;

const TableBody = styled.div`
  overflow: hidden;
  overflow-y: auto;
`;

const Recipient = styled.div`
  font-weight: bold;
  color: #1ba833;
  margin-bottom: 0.5rem;
  margin-right: 0.25rem;

  .number {
    font-weight: 500;
    letter-spacing: 1px;
  }
`;

const RecipientDetails = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .date {
    color: #969696;
  }
`;

const RecipientExpense = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-direction: row;

  p {
    margin-bottom: 0.5rem;
  }
`;

const RecipientSection = styled.div`
  border-top: 0.5px solid #979797;
`;

const CenterDiv = styled.div`
  display: flex;
  justify-content: center;
`;

const LoadPageSection = styled(CenterDiv)`
  margin-top: 1rem;
  margin-bottom: 1rem;

  button {
    border-radius: 11px;
    background-color: #ac141e;

    &:hover {
      background: #9a121b;
    }

    span {
      color: white;
    }
  }
`;

const SearchSection = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 2rem;

  .ant-input {
    width: 300px;
    border-radius: 6px;
  }
`;

const FilterIcon = styled(FilterOutlined)`
  font-size: 1.5rem;
  cursor: pointer;
`;

const FilterIconWrapper = styled.div`
  position: relative;
  display: inline-block;

  &::after {
    content: "";
    display: ${props => (props.hasActiveFilters ? "block" : "none")};
    position: absolute;
    top: -6px;
    right: -7px;
    width: 8px;
    height: 8px;
    background-color: #1890ff;
    border-radius: 50%;
  }
`;

const FilterModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 10px;
  }

  .ant-btn-primary {
    background-color: #2560fa;

    strong {
      color: white;
    }

    &:hover {
      background-color: #2560fa;
    }
  }

  .ant-btn-default {
    strong {
      color: black;
    }
  }

  @media (max-width: 768px) {
    .ant-modal {
      width: 100% !important;
      margin: 0;
      padding: 0;
      max-width: 100%;
      top: 0;
    }

    .ant-modal-content {
      min-height: 50vh;
      border-radius: 0;
    }

    .ant-modal-body {
      padding: 24px;
      height: calc(50vh - 110px); // Adjust for header and footer
    }
  }
`;

const DateFilterContainer = styled.div`
  .date-label {
    min-width: 80px;
  }
`;

const FilterBodyContainer = styled.div`
  display: flex;
  align-items: center;

  margin-top: 10px;
  gap: 15px;
`;

const TransactionLogs = () => {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [searchCitizen, setSearchCitizen] = React.useState("");
  const [showFilter, setShowFilter] = React.useState(false);
  const [dateRange, setDateRange] = React.useState({
    fromDate: null,
    toDate: null,
  });

  const user = JSON.parse(localStorage.getItem("qrUser"));

  const debouncedFetch = React.useCallback(
    debounce(searchTerm => {
      setPage(1);
      setLogs([]);
      fetchTransactions(1, searchTerm);
    }, 500),
    [dateRange],
  );

  const fetchTransactions = async (pageNumber, searchTerm = searchCitizen) => {
    setLoading(true);
    const res = await getTransactionLogs(user?.id, pageNumber, searchTerm, dateRange);

    const newItems = res?.results;

    if (pageNumber === 1) {
      setLogs(newItems || []);
    } else if (newItems?.length) {
      setLogs(prevLogs => [...prevLogs, ...newItems]);
    }

    if (res?.next) {
      setHasMore(true);
    } else {
      setHasMore(false);
    }

    setLoading(false);
  };

  const handleDateFilter = () => {
    setShowFilter(false);
    setPage(1);
    fetchTransactions(1);
  };

  const loadNextPage = () => {
    if (!loading && hasMore) {
      setPage(prevPage => prevPage + 1);
    }
  };

  const prevPageRef = React.useRef();

  React.useEffect(() => {
    if (user?.id) {
      if (prevPageRef.current !== page) {
        fetchTransactions(page);
        prevPageRef.current = page;
      }
    }
  }, [page, user]);

  const hasActiveFilters = dateRange.fromDate || dateRange.toDate;

  return (
    <TransactionTable>
      <SearchSection>
        <Input
          placeholder="Search by citizen name"
          value={searchCitizen}
          onChange={e => {
            setSearchCitizen(e.target.value);
            debouncedFetch(e.target.value);
          }}
          allowClear
          style={{ borderRadius: "10px" }}
        />
        <FilterIconWrapper hasActiveFilters={hasActiveFilters}>
          <FilterIcon onClick={() => setShowFilter(true)} />
        </FilterIconWrapper>
      </SearchSection>

      <FilterModal
        title="Filter"
        visible={showFilter}
        onCancel={() => {
          setDateRange({ fromDate: null, toDate: null });
        }}
        cancelText={<strong>Reset</strong>}
        onOk={handleDateFilter}
        okText={<strong>Apply Filter</strong>}
        closeIcon={<CloseOutlined onClick={() => setShowFilter(false)} />}>
        <span>
          <strong>Filter by Date</strong>
        </span>
        <FilterBodyContainer>
          <DateFilterContainer>
            <DatePicker
              placeholder="Date from"
              value={dateRange.fromDate}
              onChange={date => {
                setDateRange(prev => ({
                  fromDate: date,
                  toDate: prev.toDate && date?.isAfter(prev.toDate) ? null : prev.toDate,
                }));
              }}
            />
          </DateFilterContainer>
          <span className="date-label">to</span>
          <DateFilterContainer>
            <DatePicker
              placeholder="Date to"
              value={dateRange.toDate}
              disabledDate={current => dateRange.fromDate && current && current.isBefore(dateRange.fromDate, "day")}
              onChange={date => setDateRange(prev => ({ ...prev, toDate: date }))}
            />
          </DateFilterContainer>
        </FilterBodyContainer>
      </FilterModal>

      <TableHeader>
        <Col>
          <h4>Weight</h4>
        </Col>
        <Col>
          <h4>Price / kg</h4>
        </Col>
        <Col>
          <h4>Gross Total</h4>
        </Col>
        <Col style={{ textAlign: "end" }}>
          <h4>Subs. Amount</h4>
        </Col>
      </TableHeader>

      <TableBody>
        {logs?.map(log => (
          <RecipientSection key={log.id}>
            <RecipientExpense>
              <Col>
                <p>{`${log?.overrides?.total_kilos || 0}kg`}</p>
              </Col>
              <Col>
                <p>{formatMoney(log?.overrides?.price_per_kilo || 0)}</p>
              </Col>
              <Col>
                <p>{modifiedFormatMoney(log?.overrides?.gross_total || 0)}</p>
              </Col>
              <Col style={{ display: "flex", justifyContent: "flex-end" }}>
                <p>{modifiedFormatMoney(log?.overrides?.subsidized_amount || 0)}</p>
              </Col>
            </RecipientExpense>
            <RecipientDetails>
              <Recipient>
                {log?.citizen?.name}
                <div className="number">{log?.id}</div>
              </Recipient>
              <div className="date">{format(parseISO(log?.created_at), "MMM dd, yyyy h:mma")}</div>
            </RecipientDetails>
          </RecipientSection>
        ))}

        {hasMore && !loading && (
          <LoadPageSection>
            <Button onClick={() => loadNextPage()}>load more</Button>
          </LoadPageSection>
        )}

        {loading && (
          <CenterDiv>
            <Spin />
          </CenterDiv>
        )}
      </TableBody>
    </TransactionTable>
  );
};

export default TransactionLogs;
